/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Rail de acceso rápido del catálogo POS: vistas inteligentes (Combos,
 * En oferta, Paquetes, Favoritos) + categorías acotadas.
 * Categorías en el rail solo en mobile; en desktop van por Filtros → Categoría.
 */
import React, { memo, useMemo } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { CategoryOption } from '../../../catalog/domain-api';
import { useOperationalFilterLayout } from '../../../shared/filters/useOperationalFilterLayout';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import {
  posQuickAccessRailShowChipRow,
  posRailShowsCategoryControl,
} from '../../application/posQuickAccessRail';
import { posRailSectionStyles } from './posQuickAccessRail.styles';

export type PosQuickAccessRailProps = {
  selection: string;
  onSelect: (sel: string) => void;
  categories: CategoryOption[];
  counts: { favorites: number; combos: number; promos: number; packages: number };
  categoryPickerLabel?: string;
  onOpenCategorySelector?: () => void;
  /** Dentro de TicketModuleHeader: sin márgenes sueltos contra el fondo del listado. */
  embedded?: boolean;
};

type ChipDef = {
  key: string;
  label: string;
  icon?: keyof typeof Ionicons.glyphMap;
  count?: number;
  kind: 'view' | 'category' | 'categoryPicker';
};

function RailChip({
  chip,
  active,
  onSelect,
  equalWidth = false,
}: {
  chip: ChipDef;
  active: boolean;
  onSelect: (k: string) => void;
  /** Desk: reparte el ancho del catálogo entre vistas (paridad visual con fulfillment). */
  equalWidth?: boolean;
}) {
  const isCategory = chip.kind === 'category' || chip.kind === 'categoryPicker';
  return (
    <TouchableOpacity
      style={[
        styles.chip,
        equalWidth && styles.chipDeskEqual,
        isCategory && styles.chipCategory,
        chip.kind === 'categoryPicker' && styles.chipPicker,
        active && styles.chipOn,
      ]}
      onPress={() => onSelect(chip.key)}
      activeOpacity={0.6}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={chip.count != null ? `${chip.label}, ${chip.count} ítems` : chip.label}
      testID={`pos.rail.${chip.key}`}
    >
      {chip.icon ? (
        <Ionicons
          name={chip.icon}
          size={13}
          color={active ? ticketColors.brandInk : ticketColors.ink3}
        />
      ) : null}
      <Text
        style={[
          styles.chipText,
          isCategory && styles.chipTextCategory,
          active && styles.chipTextOn,
        ]}
        numberOfLines={1}
      >
        {chip.label}
      </Text>
      {chip.kind === 'categoryPicker' ? (
        <Ionicons name="chevron-down" size={14} color={active ? ticketColors.brandInk : ticketColors.ink3} />
      ) : null}
      {chip.count != null ? (
        <Text style={[styles.chipCount, active && styles.chipCountOn]}>{chip.count}</Text>
      ) : null}
    </TouchableOpacity>
  );
}

function PosQuickAccessRailInner({
  selection,
  onSelect,
  categories,
  counts,
  categoryPickerLabel = 'Todas',
  onOpenCategorySelector,
  embedded = false,
}: PosQuickAccessRailProps) {
  const { layoutWide: layoutWide } = useOperationalFilterLayout();

  const leafCategories = useMemo(
    () =>
      categories.filter((c) => {
        if (c.id === 'all') return false;
        if (counts.combos > 0 && c.label.trim().toLowerCase() === 'combos') return false;
        return true;
      }),
    [categories, counts.combos],
  );

  const showCategoryOnRail =
    onOpenCategorySelector != null &&
    posRailShowsCategoryControl(layoutWide, leafCategories.length);

  const smartChips = useMemo<ChipDef[]>(() => {
    const chips: ChipDef[] = [{ key: 'all', label: 'Todos', kind: 'view' }];
    if (counts.favorites > 0) {
      chips.push({
        key: 'favorites',
        label: 'Favoritos',
        icon: 'star-outline',
        count: counts.favorites,
        kind: 'view',
      });
    }
    if (counts.combos > 0) {
      chips.push({ key: 'combos', label: 'Combos', icon: 'albums-outline', count: counts.combos, kind: 'view' });
    }
    if (counts.promos > 0) {
      chips.push({
        key: 'promos',
        label: 'En oferta',
        icon: 'pricetag-outline',
        count: counts.promos,
        kind: 'view',
      });
    }
    if (counts.packages > 0) {
      chips.push({
        key: 'packages',
        label: 'Paquetes',
        icon: 'cube-outline',
        count: counts.packages,
        kind: 'view',
      });
    }
    return chips;
  }, [counts.favorites, counts.combos, counts.promos, counts.packages]);

  const categoryChips = useMemo<ChipDef[]>(() => {
    if (!showCategoryOnRail) return [];
    return [
      {
        key: 'cat:picker',
        label: `Categoría · ${categoryPickerLabel}`,
        kind: 'categoryPicker',
      },
    ];
  }, [categoryPickerLabel, showCategoryOnRail]);

  const onChipSelect = (key: string) => {
    if (key === 'cat:picker') {
      onOpenCategorySelector?.();
      return;
    }
    onSelect(key);
  };

  const showChipRow = posQuickAccessRailShowChipRow(
    layoutWide,
    counts,
    categoryChips.length > 0,
  );

  const deskEqualChipStrip =
    layoutWide && categoryChips.length === 0 && smartChips.length >= 2 && smartChips.length <= 6;

  /** Mobile: label ayuda; desk: chips repartidos, sin micro-título. */
  const showVistasLabel =
    !layoutWide && (smartChips.length > 1 || categoryChips.length > 0);

  const renderViewChips = (equalWidth: boolean) => (
    <>
      {smartChips.map((chip) => (
        <RailChip
          key={chip.key}
          chip={chip}
          active={selection === chip.key}
          onSelect={onChipSelect}
          equalWidth={equalWidth}
        />
      ))}
      {categoryChips.length > 0 ? <View style={styles.sep} /> : null}
      {categoryChips.map((chip) => (
        <RailChip
          key={chip.key}
          chip={chip}
          active={
            chip.kind === 'categoryPicker'
              ? selection.startsWith('cat:')
              : selection === chip.key
          }
          onSelect={onChipSelect}
          equalWidth={equalWidth}
        />
      ))}
    </>
  );

  if (!showChipRow) return null;

  return (
    <View style={[styles.stack, embedded && styles.stackEmbedded]} testID="pos.rail">
      <View style={posRailSectionStyles.chipRowBlock}>
        {showVistasLabel ? (
          <Text style={posRailSectionStyles.sectionLabel} testID="pos.rail.vistas-label">
            Vistas
          </Text>
        ) : null}
        {deskEqualChipStrip ? (
          <View style={[styles.rail, styles.railDeskEqual]}>{renderViewChips(true)}</View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.railScroll}
            contentContainerStyle={styles.rail}
            keyboardShouldPersistTaps="handled"
          >
            {renderViewChips(false)}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  stack: {
    gap: 8,
    marginTop: 4,
    marginBottom: 2,
  },
  stackEmbedded: {
    marginTop: 0,
    marginBottom: 0,
    gap: 0,
    alignSelf: 'stretch',
  },
  railScroll: {
    alignSelf: 'stretch',
    flexGrow: 0,
    flexShrink: 0,
  },
  rail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 2,
    paddingRight: 2,
  },
  railDeskEqual: {
    alignSelf: 'stretch',
    width: '100%',
    gap: 8,
    paddingVertical: 0,
    paddingRight: 0,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 34,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  chipDeskEqual: {
    flex: 1,
    minWidth: 0,
    height: 38,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chipCategory: {
    backgroundColor: 'transparent',
    borderColor: ticketColors.ruleSoft,
  },
  chipPicker: {
    maxWidth: 220,
  },
  chipOn: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorder,
    ...(Platform.OS === 'web' ? ({ boxShadow: 'inset 0 0 0 1px rgba(15,118,110,0.12)' } as object) : null),
  },
  chipText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    flexShrink: 1,
  },
  chipTextCategory: {
    color: ticketColors.ink3,
  },
  chipTextOn: {
    color: ticketColors.ink,
  },
  chipCount: {
    fontSize: 10,
    fontWeight: '700',
    fontFamily: ticketFonts.mono,
    color: ticketColors.ink3,
    backgroundColor: ticketColors.paper2,
    borderRadius: 8,
    minWidth: 16,
    textAlign: 'center',
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  chipCountOn: {
    color: ticketColors.brandInk,
    backgroundColor: ticketColors.brandBorderSoft,
  },
  sep: {
    width: 1,
    alignSelf: 'stretch',
    marginVertical: 5,
    marginHorizontal: 3,
    backgroundColor: ticketColors.ruleSoft,
  },
});

export const PosQuickAccessRail = memo(PosQuickAccessRailInner);
